import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';

/**
 * VAROTRADES — Live User Counter
 * - Persists base count + last update timestamp in localStorage
 * - Increments by 1-4 every 4-12 seconds when in view
 * - Catches up missed time after page reloads (computes elapsed seconds × avg rate)
 * - Tabular nums + spring-feel update
 */
const STORAGE_KEY = 'vt_live_users';
const DEFAULT_BASE = 1397118;
const AVG_PER_SECOND = 0.36; // ~21.6 / minute → ~31,000 / day = realistic broker scale

const LiveUserCounter = ({ className = '', size = 'lg' }) => {
 const ref = useRef(null);
 const inView = useInView(ref, { once: false, margin: '-50px' });
 const [count, setCount] = useState(DEFAULT_BASE);

 // Initial hydration: read from localStorage and add elapsed time delta
 useEffect(() => {
 try {
 const raw = localStorage.getItem(STORAGE_KEY);
 let base = DEFAULT_BASE;
 let lastTs = Date.now();
 if (raw) {
 const obj = JSON.parse(raw);
 base = Math.max(DEFAULT_BASE, obj.count || DEFAULT_BASE);
 lastTs = obj.ts || Date.now();
 }
 const elapsed = Math.max(0, (Date.now() - lastTs) / 1000);
 const delta = Math.floor(elapsed * AVG_PER_SECOND);
 const initial = base + delta;
 setCount(initial);
 localStorage.setItem(STORAGE_KEY, JSON.stringify({ count: initial, ts: Date.now() }));
 } catch { /* noop */ }
 }, []);

 // Tick — increment randomly 1-4 every 4-12 seconds
 useEffect(() => {
 if (!inView) return;
 let timer;
 const tick = () => {
 setCount((prev) => {
 const inc = 1 + Math.floor(Math.random() * 4);
 const next = prev + inc;
 try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ count: next, ts: Date.now() })); } catch {}
 return next;
 });
 const nextDelay = 4000 + Math.random() * 8000;
 timer = setTimeout(tick, nextDelay);
 };
 timer = setTimeout(tick, 2200);
 return () => clearTimeout(timer);
 }, [inView]);

 const sizes = {
 lg: 'text-4xl lg:text-5xl',
 xl: 'text-5xl lg:text-6xl',
 md: 'text-3xl lg:text-4xl',
 };

 return (
 <span ref={ref} className={`relative inline-flex items-center ${className}`}>
 {/* live pulse dot */}
 <span className="me-3 relative flex w-2 h-2">
 <span className="absolute inline-flex h-full w-full rounded-full bg-flame-500 opacity-60 animate-ping" />
 <span className="relative inline-flex rounded-full h-2 w-2 bg-flame-500" />
 </span>
 <span className={`font-black tracking-[-0.04em] text-ink-900 font-mono tabular-nums ${sizes[size] || sizes.lg}`}>
 {count.toLocaleString()}
 </span>
 </span>
 );
};

export default LiveUserCounter;
